/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect } from 'react';
import { AppBar, Tabs, Tab, Typography } from '@mui/material';
import { Tokens } from '@jda/lui-common-component-library-mui5';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { useStyles } from './styles';
import { IRole, IRoleSelected, TabPanelProps } from '../../models/Roles/RolesContracts';
import Policies from './Policies';
import AssignedUsers from "./AssignedUsers";
import { permissions, actions, resources } from '../../shared/CheckPermissions/constants';
import { useSelector } from 'react-redux';
import { selectAllAssignedPermissions, selectResourcePermissions } from '../../store/auth/authSelector';
import { checkPermission, checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { POLICIES, ASSIGNED_USERS, LOCATIONS } from "./constants";
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import Location from "./Location";

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Typography component="div" role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`}>
      {value === index && <>{children}</>}
    </Typography>
  );
}

const RoleDetails = ({ selectedItem }: IRoleSelected) => {
  const { classes } = useStyles();
  const [value, setValue] = React.useState(0);
  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const isLocationEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.location);
  const appLocation = useSelector(selectAppLocation);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const readPolicyPermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission(
      [
        permissions.role.read,
        permissions.role.read_write,
        permissions.role.read_assign,
        permissions.policy.read,
        permissions.policy.read_assign
      ],
      assignedPermissions.permissions
    );
  const readRolePolicy =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.ROLES]);
  const readLocationPolicy =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.LOCATIONS]);

  useEffect(() => {
    setValue(0);
  }, [selectedItem.id]);

  const roleDetailTabs = [
    readPolicyPermission
      ? [
        {
          name: POLICIES,
          tabId: 'role-details-tab-policies',
          tabPanelTestId: 'roles-tab-policies',
          component: (item: IRole) => <Policies selectedItem={item} />
        }
      ]
      : [],
    readPolicyPermission
      ? [
        {
          name: ASSIGNED_USERS,
          tabId: 'role-details-tab-users',
          tabPanelTestId: 'roles-tab-assigned-users',
          component: (item: IRole) => <AssignedUsers selectedItem={item} />
        }
      ]
      : [],
    isLocationEnabled && appLocation !== null && readRolePolicy && readLocationPolicy
      ? [
        {
          name: LOCATIONS,
          tabId: 'role-details-tab-locations',
          tabPanelTestId: 'roles-tab-locations',
          component: (item: IRole) => <Location selectedItem={item} />
        }
      ]
      : []
  ].flat();

  return (
    <>
      <AppBar id="role-details-appbar" elevation={0} position="static" classes={{ root: classes.rootTab }}>
        <Tabs
          value={value}
          onChange={handleChange}
          data-testid="role-details-tabs"
          TabIndicatorProps={{ style: { height: Tokens.spacing['$sp-0-5'] } }}
        >
          {roleDetailTabs.map(({ name, tabId }) =>
            <Tab
              key={tabId}
              id={tabId}
              label={name}
              sx={{ minWidth: '160px' }}
            />
          )}
        </Tabs>
      </AppBar>
      {roleDetailTabs.map(({ component, tabPanelTestId }, index) => (
        <TabPanel key={tabPanelTestId} data-testid={tabPanelTestId} value={value} index={index}>
          {typeof component === 'function' ? component(selectedItem) : component}
        </TabPanel>
      ))}
    </>
  );
};

export default RoleDetails;
